/**
 * spaceLevels.ts
 * Per-level configuration for World 2 (Starfield Drift) space-shmup stages.
 *
 * The space scene's defaults (auto-scroll speed, Poisson spawner intervals,
 * pool caps) live in `src/constants/spaceConstants.ts`,
 * `src/constants/asteroidConstants.ts`, and
 * `src/constants/spaceEnemyConstants.ts`. This module layers per-level
 * *gating* and *end conditions* on top — it does not redefine the tuning
 * constants themselves.
 *
 * Each level config decides:
 *   - which spawner systems run this level (asteroids / enemies / boss),
 *   - when the level ends (duration, or boss defeat — extensible).
 *
 * SpaceScene.init(config) stores the config on `spaceState.levelConfig`;
 * `stepSpace` reads it to gate spawner calls and watch the end condition.
 *
 * Phase 4 — World 2: Starfield Drift.
 * See ROADMAP.md Phase 4 "Level 2-1: Asteroid Field onboarding".
 *
 * @module spaceLevels
 */

import type {
  SpaceLevelConfig,
  SpaceLevelEndCondition,
} from './spaceLevelTypes';
import {
  SPACE_LEVEL_2_2_WAVE_SCRIPT,
  SPACE_LEVEL_2_3_WAVE_SCRIPT,
  SPACE_LEVEL_2_4_WAVE_SCRIPT,
  SPACE_LEVEL_2_S_WAVE_SCRIPT,
} from './spaceWaveScripts';

export type { SpaceLevelConfig, SpaceLevelEndCondition };

/**
 * Level 2-1 — Asteroid Field onboarding.
 *
 * The first World 2 stage. Player learns to maneuver the ship + shoot
 * asteroids with no enemy pressure. ~60 seconds long; ends on duration.
 *
 * Tuning notes:
 *  - Uses the default asteroid spawner cadence
 *    (`ASTEROID_SPAWN_INTERVAL_S` in `asteroidConstants.ts`). Per-level
 *    density overrides are a later enhancement.
 *  - No enemies, no boss — pure obstacle-dodge / shoot practice.
 */
export const SPACE_LEVEL_2_1: SpaceLevelConfig = {
  id: '2-1',
  name: 'Asteroid Field',
  durationSeconds: 60,
  asteroidsEnabled: true,
  enemiesEnabled: false,
  bossEnabled: false,
  endCondition: 'duration',
  shards: [
    { id: '2-1-shard-1', x: 1400, y: 80 },
    { id: '2-1-shard-2', x: 2600, y: 200 },
    { id: '2-1-shard-3', x: 3800, y: 120 },
  ],
};

/**
 * Level 2-2 — Enemy Waves.
 *
 * The second World 2 stage. Player faces fighter drones (and the rest of the
 * shared enemy pool) without asteroid pressure so the focus is on dodging
 * enemy fire + downing waves. ~60 seconds long; ends on duration.
 *
 * Tuning notes:
 *  - Poisson enemy spawner is OFF (`enemiesEnabled: false`); all enemies
 *    arrive via the scheduled `waveScript` below. This keeps the wave
 *    pacing readable instead of stacking RNG spawns on top of waves.
 *  - No asteroids, no boss — pure enemy-wave practice.
 *  - Wave script tuned for a 60s level with seven waves: three fighter
 *    sweeps (line / v / line), a bomber pair, an arc of fighters, a v of
 *    satellites for variety, and a closing v of fighters. All `atSeconds`
 *    are strictly ascending so the scheduler's monotonic cursor is valid.
 *    Wave array lives in `spaceWaveScripts.ts`.
 */
export const SPACE_LEVEL_2_2: SpaceLevelConfig = {
  id: '2-2',
  name: 'Enemy Waves',
  durationSeconds: 60,
  asteroidsEnabled: false,
  enemiesEnabled: false,
  bossEnabled: false,
  endCondition: 'duration',
  waveScript: SPACE_LEVEL_2_2_WAVE_SCRIPT,
  shards: [
    { id: '2-2-shard-1', x: 1500, y: 130 },
    { id: '2-2-shard-2', x: 2900, y: 70 },
    { id: '2-2-shard-3', x: 4100, y: 210 },
  ],
};

/**
 * Level 2-3 — Nebula Gauntlet.
 *
 * The third World 2 stage. Pushes the wave system harder than 2-2: longer
 * runtime (~80s), denser pacing, more bombers and shield satellites, and
 * a closing "all-hands" surge to stress-test player resource management.
 *
 * Tuning notes:
 *  - Poisson spawner stays OFF (`enemiesEnabled: false`) so the wave
 *    cadence is deterministic and tunable.
 *  - Asteroids + boss remain off. Mines + low-visibility fog (the eventual
 *    nebula-gauntlet flavour from the ROADMAP) are a follow-up slice —
 *    this level uses the existing enemy pool only.
 *  - Wave count goes from 7 (2-2) to 11. Average spacing drops from ~8s
 *    to ~6.5s, with a deliberate sub-3s burst near the end (waves 9/10/11)
 *    to give the closer a different feel from the steady mid-section.
 *  - All `atSeconds` strictly ascending (scheduler contract).
 *    Wave array lives in `spaceWaveScripts.ts`.
 */
export const SPACE_LEVEL_2_3: SpaceLevelConfig = {
  id: '2-3',
  name: 'Nebula Gauntlet',
  durationSeconds: 80,
  asteroidsEnabled: false,
  enemiesEnabled: false,
  bossEnabled: false,
  endCondition: 'duration',
  waveScript: SPACE_LEVEL_2_3_WAVE_SCRIPT,
  shards: [
    { id: '2-3-shard-1', x: 1800, y: 200 },
    { id: '2-3-shard-2', x: 3600, y: 80 },
    { id: '2-3-shard-3', x: 5200, y: 160 },
  ],
};

/**
 * Level 2-4 — Boss Approach.
 *
 * The final World 2 lead-in before the VoidMothership. Mixes hazards:
 * asteroids return alongside scripted enemy waves so the player has to
 * juggle obstacle dodging + offensive priority for the first time in
 * World 2. The boss itself is NOT triggered here (`bossEnabled: false`)
 * — that fight gets its own level config (next PR) so the approach
 * stage and the boss arena can be tuned and tested independently.
 *
 * Tuning notes:
 *  - 70 seconds long, ends on duration.
 *  - Asteroids ON to provide constant ambient pressure (uses the
 *    default `SPACE_ASTEROID_*` cadence — no per-level tuning yet).
 *  - Poisson enemy spawner OFF; waves drive enemy spawn timing so the
 *    intensity curve is deterministic.
 *  - Wave count: 10 entries. Shorter than 2-3 because the asteroid
 *    layer is already taking attention. Closing beat is a Star-Eater
 *    scout fleet — small/fast/dark scouts that foreshadow the World 5
 *    boss and give the player a glimpse of the predator-class threat
 *    before the VoidMothership fight on 2-B.
 *  - All `atSeconds` strictly ascending (scheduler contract).
 *    Wave array lives in `spaceWaveScripts.ts`.
 */
export const SPACE_LEVEL_2_4: SpaceLevelConfig = {
  id: '2-4',
  name: 'Boss Approach',
  durationSeconds: 70,
  asteroidsEnabled: true,
  enemiesEnabled: false,
  bossEnabled: false,
  endCondition: 'duration',
  waveScript: SPACE_LEVEL_2_4_WAVE_SCRIPT,
  shards: [
    { id: '2-4-shard-1', x: 1600, y: 110 },
    { id: '2-4-shard-2', x: 3200, y: 220 },
    { id: '2-4-shard-3', x: 4600, y: 60 },
  ],
};

/**
 * Level 2-S — Secret stage: "Drift Field".
 *
 * The hidden World 2 stage. Per ROADMAP: exploration-focused with light
 * enemies. World 2 has no in-mission collectible primitive yet, so the
 * "dense collectibles" half of the brief translates to dense ambient
 * asteroid debris — the only exploration-flavoured primitive available
 * in the space scene. The asteroid spawner stays at its default cadence,
 * so the field reads as denser only because nothing else competes for
 * the player's attention.
 *
 * Tuning notes:
 *  - 90 seconds long, ends on duration — longest World 2 stage by 10s
 *    over 2-3 to reinforce the "drift" / exploration pacing.
 *  - Asteroids ON. Poisson enemy spawner OFF so the cadence is fully
 *    deterministic and the level can stay quiet on purpose.
 *  - Wave count: 5 entries, all small formations spread thin. Total
 *    enemies (~16) is roughly a third of 2-4's, hitting the "light
 *    enemy pressure" requirement. No bombers / no Star-Eaters — those
 *    belong to the main-line difficulty curve.
 *  - No boss.
 *  - All `atSeconds` strictly ascending (scheduler contract).
 *
 * Unlock gate: 12 Constellation Shards collected across 2-1 .. 2-4
 * (3 per level). Each main level's `shards` config drives spawn; the
 * pre-existing `isSecretStageUnlocked(save, 2)` gate in
 * {@link ../data/worldUnlock} counts them via
 * {@link ../data/worldProgress.countCollectedShards} and unlocks 2-S
 * once the player has all `SHARDS_PER_WORLD` (= 12). The `devUnlockWorld2`
 * Settings toggle continues to surface the stage for playtesting
 * without grinding the shard hunt.
 *
 * Wave array lives in `spaceWaveScripts.ts`.
 */
export const SPACE_LEVEL_2_S: SpaceLevelConfig = {
  id: '2-S',
  name: 'Drift Field',
  durationSeconds: 90,
  asteroidsEnabled: true,
  enemiesEnabled: false,
  bossEnabled: false,
  endCondition: 'duration',
  waveScript: SPACE_LEVEL_2_S_WAVE_SCRIPT,
};

/**
 * Level 2-Boss — Void Mothership.
 *
 * Dedicated boss arena: no asteroids, no scripted waves, no Poisson
 * spawner. Only the VoidMothership is active. The fight ends when the
 * boss singleton reaches its `DEFEATED` state — `durationSeconds` is
 * `null` so the duration watcher never fires here.
 *
 * Tuning notes:
 *  - `VOID_MOTHERSHIP_TRIGGER_TIME_S` (currently 30s) governs how long
 *    the player flies through empty space before the mothership warps
 *    in. That delay is a constant on the boss system, not part of this
 *    level config — re-tuning it is a separate slice.
 *  - The boss handles its own multi-phase loop (turret + drones +
 *    shield window + ram + missiles). No additional gating needed here.
 *  - Asteroids and scripted waves are intentionally OFF so the fight
 *    isn't competing with ambient pressure. If we later want a "second
 *    wind" wave during the boss fight, add a waveScript here and the
 *    scheduler will fire it in parallel.
 */
export const SPACE_LEVEL_BOSS: SpaceLevelConfig = {
  id: '2-B',
  name: 'Void Mothership',
  durationSeconds: null,
  asteroidsEnabled: false,
  enemiesEnabled: false,
  bossEnabled: true,
  endCondition: 'boss-defeated',
};

/**
 * Default config used when SpaceScene.init() is called with no argument.
 * Matches the pre-config behaviour: everything enabled, no end condition.
 * Existing tests and any future ad-hoc playtest path keep working unchanged.
 *
 * DECISION: A null `durationSeconds` paired with `endCondition: 'duration'`
 * is the explicit "never end" signal. `stepSpace` treats `null` as
 * never-trigger so the watcher branch stays simple.
 */
export const SPACE_LEVEL_DEFAULT: SpaceLevelConfig = {
  id: '__default__',
  name: 'Default (all systems on, no end)',
  durationSeconds: null,
  asteroidsEnabled: true,
  enemiesEnabled: true,
  bossEnabled: true,
  endCondition: 'duration',
};

/**
 * Registry of World 2 levels keyed by id. Add new levels here and they're
 * immediately discoverable by future level-select UI / world-map wiring.
 *
 * Typed as `Partial<Record<string, ...>>` so that arbitrary string lookups
 * return `SpaceLevelConfig | undefined` rather than a falsely-narrowed
 * `SpaceLevelConfig`. This forces callers to handle unknown ids explicitly
 * — without this, a typo'd id would silently launch the endless default
 * via `SpaceScene.init(undefined)`. Prefer `getSpaceLevel(id)` over
 * direct index access for the same reason at the call site.
 */
export const SPACE_LEVELS: Readonly<Partial<Record<string, SpaceLevelConfig>>> = {
  [SPACE_LEVEL_2_1.id]: SPACE_LEVEL_2_1,
  [SPACE_LEVEL_2_2.id]: SPACE_LEVEL_2_2,
  [SPACE_LEVEL_2_3.id]: SPACE_LEVEL_2_3,
  [SPACE_LEVEL_2_4.id]: SPACE_LEVEL_2_4,
  [SPACE_LEVEL_2_S.id]: SPACE_LEVEL_2_S,
  [SPACE_LEVEL_BOSS.id]: SPACE_LEVEL_BOSS,
};

/**
 * Look up a World 2 level by id. Returns `undefined` for unknown ids;
 * callers must explicitly handle the missing case rather than passing
 * the undefined through to `SpaceScene.init` (which falls back to the
 * endless default).
 *
 * @param id - The level id, e.g. `'2-1'`.
 * @returns The matching `SpaceLevelConfig`, or `undefined` if not found.
 */
export function getSpaceLevel(id: string): SpaceLevelConfig | undefined {
  return SPACE_LEVELS[id];
}
